import DialpadIcon from "@mui/icons-material/Dialpad";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import BackspaceIcon from "@mui/icons-material/Backspace";
import Input from "@mui/material/Input";
import PhoneMaskedInput from "components/input-form-field/phone-masked-input.component";
import useSunoDialer from "components/suno-dialer/use-suno-dialer.hook";
import DigitButton from "../../digit-button.component";
import Stack from "@mui/material/Stack";
import { useCallback, useMemo, useRef } from "react";
import CallButton from "components/suno-dialer/call-patient/call-button.component";
import {
  DEFAULT_PATIENTS_QUERY,
  useLazyQueryPatientsQuery,
} from "services/patients";
import { normalizePhoneNumber } from "components/suno-dialer/utils";

export default function PhoneInputGroup() {
  const {
    outgoingCall,
    handleMakeCall,
    handleChangeOutgoingCallPhoneNumberInput,
    handleChangeOutgoingCallPatient,
    handleToggleShowPatients,
  } = useSunoDialer();

  const [queryPatients] = useLazyQueryPatientsQuery();

  const showCallButton = useMemo(() => {
    const value = normalizePhoneNumber(outgoingCall?.phone_number);
    return value.length >= 10;
  }, [outgoingCall?.phone_number]);

  const dialInProgress = Boolean(outgoingCall?.dialInProgress);
  const inputRef = useRef<any>();

  const handleCallButtonClick = useCallback(async () => {
    handleMakeCall(
      String(outgoingCall?.phone_number || ""),
      outgoingCall?.patient?.id
    );

    if (!outgoingCall?.patient) {
      const { data } = await queryPatients(
        {
          search: normalizePhoneNumber(outgoingCall?.phone_number),
          baseFilter: {},
          is_active: true,
          limit: 1,
          query: DEFAULT_PATIENTS_QUERY,
        },
        false
      );

      const patient = data?.length ? data[0] : undefined;

      if (outgoingCall && patient) {
        handleChangeOutgoingCallPatient(patient);
      }
    }
  }, [
    handleChangeOutgoingCallPatient,
    handleMakeCall,
    outgoingCall,
    queryPatients,
  ]);

  return (
    <>
      <Stack
        direction="row"
        sx={{ mb: 0, ml: 4, mt: -0.5 }}
        display="flex"
        justifyContent="center"
        spacing={1}
      >
        <Input
          autoFocus
          inputRef={inputRef}
          size="small"
          inputComponent={PhoneMaskedInput as any}
          value={outgoingCall?.phone_number ?? ""}
          onChange={(event) => {
            handleChangeOutgoingCallPhoneNumberInput(event.target.value);
          }}
          readOnly={dialInProgress}
          sx={{
            color: "white",
            fontWeight: 700,
            ".MuiInputBase-inputSizeSmall": {
              letterSpacing: "1px",
              fontSize: "18px",
              textAlign: "center",
            },
            ":before": {
              borderBottomColor: "white",
              opacity: dialInProgress ? 0 : 0.5,
            },
            ":after": {
              borderBottomColor: "white",
              opacity: dialInProgress ? 0 : 0.5,
            },
          }}
        />

        <DigitButton
          title={<BackspaceIcon />}
          onClick={
            dialInProgress
              ? undefined
              : () => {
                  handleChangeOutgoingCallPhoneNumberInput(
                    (outgoingCall?.phone_number ?? "").slice(0, -1)
                  );
                  inputRef.current?.focus();
                }
          }
          sx={{
            opacity: dialInProgress ? 0 : 1,
            background: "hsla(272, 18%, 48%, 0.95)",
            boxShadow: "none",
            transform: "scale(0.7)",
          }}
        />
      </Stack>
      {!outgoingCall?.dialInProgress && (
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={1.5} justifyContent="center">
            <DigitButton
              title="1"
              subtitle={<>&nbsp;</>}
              onClick={() => {
                handleChangeOutgoingCallPhoneNumberInput(
                  `${outgoingCall?.phone_number ?? ""}1`
                );
                inputRef.current?.focus();
              }}
            />
            <DigitButton
              title="2"
              subtitle="abc"
              onClick={() => {
                handleChangeOutgoingCallPhoneNumberInput(
                  `${outgoingCall?.phone_number ?? ""}2`
                );
              }}
            />
            <DigitButton
              title="3"
              subtitle="def"
              onClick={() => {
                handleChangeOutgoingCallPhoneNumberInput(
                  `${outgoingCall?.phone_number ?? ""}3`
                );
                inputRef.current?.focus();
              }}
            />
          </Stack>
          <Stack direction="row" spacing={1.5} justifyContent="center">
            <DigitButton
              title="4"
              subtitle="ghi"
              onClick={() => {
                handleChangeOutgoingCallPhoneNumberInput(
                  `${outgoingCall?.phone_number ?? ""}4`
                );
                inputRef.current?.focus();
              }}
            />
            <DigitButton
              title="5"
              subtitle="jkl"
              onClick={() => {
                handleChangeOutgoingCallPhoneNumberInput(
                  `${outgoingCall?.phone_number ?? ""}5`
                );
                inputRef.current?.focus();
              }}
            />
            <DigitButton
              title="6"
              subtitle="mno"
              onClick={() => {
                handleChangeOutgoingCallPhoneNumberInput(
                  `${outgoingCall?.phone_number ?? ""}6`
                );
                inputRef.current?.focus();
              }}
            />
          </Stack>
          <Stack direction="row" spacing={1.5} justifyContent="center">
            <DigitButton
              title="7"
              subtitle="pqrs"
              onClick={() => {
                handleChangeOutgoingCallPhoneNumberInput(
                  `${outgoingCall?.phone_number ?? ""}7`
                );
                inputRef.current?.focus();
              }}
            />
            <DigitButton
              title="8"
              subtitle="tuv"
              onClick={() => {
                handleChangeOutgoingCallPhoneNumberInput(
                  `${outgoingCall?.phone_number ?? ""}8`
                );
                inputRef.current?.focus();
              }}
            />
            <DigitButton
              title="9"
              subtitle="wxyz"
              onClick={() => {
                handleChangeOutgoingCallPhoneNumberInput(
                  `${outgoingCall?.phone_number ?? ""}9`
                );
                inputRef.current?.focus();
              }}
            />
          </Stack>
          <Stack direction="row" spacing={1.5} justifyContent="center">
            <CallButton
              onClick={showCallButton ? handleCallButtonClick : undefined}
              sx={{
                opacity: showCallButton ? 1 : 0,
              }}
            />
            <DigitButton
              title="0"
              onClick={() => {
                handleChangeOutgoingCallPhoneNumberInput(
                  `${outgoingCall?.phone_number ?? ""}0`
                );
                inputRef.current?.focus();
              }}
            />
            <DigitButton
              title={
                outgoingCall?.displayPatients ? (
                  <DialpadIcon />
                ) : (
                  <PeopleAltIcon />
                )
              }
              onClick={handleToggleShowPatients}
              sx={{
                background: "hsla(0, 0%, 100%,0.85)",
                color: "hsla(272, 18%, 48%, 0.95)",
                "&:hover": {
                  color: "hsla(271, 18%, 27%, 0.85)",
                  background: "hsla(0, 0%, 100%,1)",
                },
              }}
            />
          </Stack>
        </Stack>
      )}
    </>
  );
}
